import { Rows } from "../../../interfaces";
import { formats, getCell, withCellStyles } from "../../../utils";
import { getHeadersForDisplayForFundPerformance, getHeadersForLogicForFundPerformance } from "./headers";
import { emptyArrayForSpace, multipleGroupGrandTotalRowIndex, onlyOneGroup, singleGroupTotalIndex } from "./mapping";
import { boldBottomBorderThickTopThinRight, rightBoldGrayForHeaders } from "../../../excelStyles";

export const getFundPerformance = (newData: Rows): Rows => {
  const designedHeaderRow = [];
  getHeadersForDisplayForFundPerformance().forEach((header) => designedHeaderRow.push(withCellStyles(header, rightBoldGrayForHeaders)));
  newData.push(designedHeaderRow);

  const numberOfCompaniesRow = getRowNumberOfCompanies();
  newData.push(...numberOfCompaniesRow);
  const writeOffRow = getRowWriteOff();
  newData.push(...writeOffRow);
  const exitRow = getRowExits();
  newData.push(...exitRow);
  const averageCheckRow = getRowAverageCheck();
  newData.push(...averageCheckRow);
  const totalInvestmentRow = getRowTotalInvestment();
  newData.push(...totalInvestmentRow);
  const totalProccedsRow = getRowTotalProceeds();
  newData.push(...totalProccedsRow);
  const gainLossRow = getRowGainLoss();
  newData.push(...gainLossRow);
  const moicRow = getRowMoic();
  newData.push(...moicRow);
  const netGainLossRow = getRowNetGainLoss();
  newData.push(...netGainLossRow);
  const tvpiRow = getRowTvpi();
  newData.push(...tvpiRow);
  const carryIntterstRow = getRowCarryInterest();
  newData.push(...carryIntterstRow);
  const tvpiNet = getRowTvpiNet();
  newData.push(...tvpiNet);

  newData.push(emptyArrayForSpace);
  newData.push(emptyArrayForSpace);
  return newData;
};

const getRowNumberOfCompanies = (): Rows => {
  const rows: Rows = [];
  const mappedToHeaders = getNewMappingNumberOfCompanies();
  for (const mapped of mappedToHeaders) {
    const row = [];
    for (const header of getHeadersForLogicForFundPerformance()) {
      row.push(withCellStyles(mapped[header], null));
    }
    rows.push(row);
  }
  return rows;
};

const getNewMappingNumberOfCompanies = (): object[] => {
  const body = {
    '': '# of companies',
    'Fund performance': getCell({
      f: onlyOneGroup ? `D${singleGroupTotalIndex}` : `D${multipleGroupGrandTotalRowIndex}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
  };
  return [body];
};

const getRowWriteOff = (): Rows => {
  const rows: Rows = [];
  const mappedToHeaders = getNewMappingWriteOff();
  for (const mapped of mappedToHeaders) {
    const row = [];
    for (const header of getHeadersForLogicForFundPerformance()) {
      row.push(withCellStyles(mapped[header], null));
    }
    rows.push(row);
  }
  return rows;
};

const getNewMappingWriteOff = (): object[] => {
  const body = {
    '': 'Write/off',
    'Fund performance': getCell({
      f: onlyOneGroup ? `E${singleGroupTotalIndex}` : `E${multipleGroupGrandTotalRowIndex}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'empty': null,
    'empty(0)': getCell({
      f: `IFERROR(B11/B10,0)`,
      z: formats.percentsRounded,
      t: 'n',
    }),
  };
  return [body];
};

const getRowExits = (): Rows => {
  const rows: Rows = [];
  const mappedToHeaders = getNewMappingExit();
  for (const mapped of mappedToHeaders) {
    const row = [];
    for (const header of getHeadersForLogicForFundPerformance()) {
      row.push(withCellStyles(mapped[header], null));
    }
    rows.push(row);
  }
  return rows;
};

const getNewMappingExit = (): object[] => {
  const body = {
    '': 'Exit',
    'Fund performance': getCell({
      f: onlyOneGroup ? `F${singleGroupTotalIndex}` : `F${multipleGroupGrandTotalRowIndex}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'empty': null,
    'empty(0)': getCell({
      f: `IFERROR(B12/B10,0)`,
      z: formats.percentsRounded,
      t: 'n',
    }),
  };
  return [body];
};

const getRowAverageCheck = (): Rows => {
  const rows: Rows = [];
  const mappedToHeaders = getNewMappingAverageCheck();
  for (const mapped of mappedToHeaders) {
    const row = [];
    for (const header of getHeadersForLogicForFundPerformance()) {
      row.push(withCellStyles(mapped[header], null));
    }
    rows.push(row);
  }
  return rows;
};

const getNewMappingAverageCheck = (): object[] => {
  const body = {
    '': 'Average check',
    'Fund performance': getCell({
      f: `IFERROR(B14/B10,0)`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
  };
  return [body];
};

const getRowTotalInvestment = (): Rows => {
  const rows: Rows = [];
  const mappedToHeaders = getNewMappingTotalInvestment();
  for (const mapped of mappedToHeaders) {
    const row = [];
    for (const header of getHeadersForLogicForFundPerformance()) {
      row.push(withCellStyles(mapped[header], null));
    }
    rows.push(row);
  }
  return rows;
};

const getNewMappingTotalInvestment = (): object[] => {
  const body = {
    '': 'Total investment',
    'Fund performance': getCell({
      f: `B5`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
  };
  return [body];
};

const getRowTotalProceeds = (): Rows => {
  const rows: Rows = [];
  const mappedToHeaders = getNewMappingTotalProceeds();
  for (const mapped of mappedToHeaders) {
    const row = [];
    for (const header of getHeadersForLogicForFundPerformance()) {
      row.push(withCellStyles(mapped[header], null));
    }
    rows.push(row);
  }
  return rows;
};

const getNewMappingTotalProceeds = (): object[] => {
  const body = {
    '': 'Total proceeds',
    'Fund performance': getCell({
      f: onlyOneGroup ? `I${singleGroupTotalIndex}` : `I${multipleGroupGrandTotalRowIndex}`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
  };
  return [body];
};

const getRowGainLoss = (): Rows => {
  const rows: Rows = [];
  const mappedToHeaders = getNewMappingGainLoss();
  for (const mapped of mappedToHeaders) {
    const row = [];
    for (const header of getHeadersForLogicForFundPerformance()) {
      row.push(withCellStyles(mapped[header], null));
    }
    rows.push(row);
  }
  return rows;
};

const getNewMappingGainLoss = (): object[] => {
  const body = {
    '': 'Portfolio gain/(loss)',
    'Fund performance': getCell({
      f: `B15-B14`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
  };
  return [body];
};

const getRowMoic = (): Rows => {
  const rows: Rows = [];
  const mappedToHeaders = getNewMappingMoic();
  for (const mapped of mappedToHeaders) {
    const row = [];
    for (const header of getHeadersForLogicForFundPerformance()) {
      row.push(withCellStyles(mapped[header], boldBottomBorderThickTopThinRight));
    }
    rows.push(row);
  }
  return rows;
};

const getNewMappingMoic = (): object[] => {
  const body = {
    '': 'MOIC',
    'Fund performance': getCell({
      f: `IFERROR(B15/B14,0)`,
      z: formats.multiplier,
      t: 'n',
    }),
  };
  return [body];
};

const getRowNetGainLoss = (): Rows => {
  const rows: Rows = [];
  const mappedToHeaders = getNewMappingNetGainLoss();
  for (const mapped of mappedToHeaders) {
    const row = [];
    for (const header of getHeadersForLogicForFundPerformance()) {
      row.push(withCellStyles(mapped[header], null));
    }
    rows.push(row);
  }
  return rows;
};

const getNewMappingNetGainLoss = (): object[] => {
  const body = {
    '': 'Net Gain/(Loss)',
    'Fund performance': getCell({
      f: `B16-B4`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
  };
  return [body];
};

const getRowTvpi = (): Rows => {
  const rows: Rows = [];
  const mappedToHeaders = getNewMappingTvpi();
  for (const mapped of mappedToHeaders) {
    const row = [];
    for (const header of getHeadersForLogicForFundPerformance()) {
      row.push(withCellStyles(mapped[header], boldBottomBorderThickTopThinRight));
    }
    rows.push(row);
  }
  return rows;
};

const getNewMappingTvpi = (): object[] => {
  const body = {
    '': 'TVPI',
    'Fund performance': getCell({
      f: `IFERROR((B18+MIN(B3,SUM(B4:B5)))/MIN(B3,SUM(B4:B5)),0)`,
      z: formats.multiplier,
      t: 'n',
    }),
  };
  return [body];
};

const getRowCarryInterest = (): Rows => {
  const rows: Rows = [];
  const mappedToHeaders = getNewMappingCarryInterest();
  for (const mapped of mappedToHeaders) {
    const row = [];
    for (const header of getHeadersForLogicForFundPerformance()) {
      row.push(withCellStyles(mapped[header], null));
    }
    rows.push(row);
  }
  return rows;
};

const getNewMappingCarryInterest = (): object[] => {
  const body = {
    '': 'Carry interest',
    'Fund performance': getCell({
      f: `IF(B18>0,B18*D20,0)`,
      z: formats.thousandsSeparator,
      t: 'n',
    }),
    'empty': null,
    'empty(0)': getCell({
      v: 0.2,
      z: formats.percents,
      t: 'n',
    }),

  };
  return [body];
};

const getNewMappingTvpiNet = (): object[] => {
  const body = {
    '': 'TVPI net from carry',
    'Fund performance': getCell({
      f: `IFERROR((B18+MIN(B3,SUM(B4:B5))-B20)/MIN(B3,SUM(B4:B5)),0)`,
      z: formats.multiplier,
      t: 'n',
    }),
  };
  return [body];
};

const getRowTvpiNet = (): Rows => {
  const rows: Rows = [];
  const mappedToHeaders = getNewMappingTvpiNet();
  for (const mapped of mappedToHeaders) {
    const row = [];
    for (const header of getHeadersForLogicForFundPerformance()) {
      row.push(withCellStyles(mapped[header], boldBottomBorderThickTopThinRight));
    }
    rows.push(row);
  }
  return rows;
};

